import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { createUser, listTenants, type Tenant } from "../../lib/auth";
import { fetchHealth } from "../../lib/api";

/** Delay before the modal auto-closes after a successful create (#87), in ms. */
const AUTO_CLOSE_MS = 1200;

/**
 * Admin-only modal to create a user account (#49/#50). Tenant is chosen from the
 * predefined list (#93) — never free-typed — so the audit-stable key cannot be
 * mistyped. On success the modal shows a brief confirmation and closes itself
 * (#87).
 */
export function AdminCreateUser({
  defaultTenant,
  onClose
}: {
  defaultTenant: string;
  onClose: () => void;
}): JSX.Element {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState(defaultTenant);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [role, setRole] = useState("end_user");
  // KB clearance level (FEATURE_KB_ADMIN). The field only shows when the flag is
  // on; default "public" is sent regardless (harmless when the gate is off).
  const [clearance, setClearance] = useState<"public" | "internal">("public");
  const [kbAdmin, setKbAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const closeTimer = useRef<number | null>(null);

  // Load the predefined tenants for the dropdown (#93). The admin's own tenant
  // is kept selected; it is always one of the predefined values.
  useEffect(() => {
    let alive = true;
    void listTenants().then((list) => {
      if (alive) setTenants(list);
    });
    void fetchHealth().then((h) => {
      if (alive && h.kind === "ok" && h.health.features?.kbAdmin === true) setKbAdmin(true);
    });
    return () => {
      alive = false;
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    };
  }, []);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setOk(null);
    const r = await createUser({ username: username.trim(), password, tenant: tenant.trim(), role, ...(kbAdmin ? { clearance } : {}) });
    setBusy(false);
    if (r.ok) {
      // Confirm with a clean success line, then auto-close so the admin isn't
      // left staring at a modal that looks like nothing happened (#87).
      setOk(`用户 ${username.trim()} 创建成功`);
      closeTimer.current = window.setTimeout(onClose, AUTO_CLOSE_MS);
    } else {
      setError(r.reason);
    }
  };

  // While the success timer is counting down the form is locked.
  const locked = busy || ok !== null;

  return (
    <div className="admin-overlay" onClick={onClose}>
      <form className="login-card" onClick={(e) => e.stopPropagation()} onSubmit={(e) => void submit(e)}>
        <h1 className="login-title">创建用户</h1>
        <p className="login-sub">管理员为客户分配账号(无公开注册)</p>
        <label className="login-field">
          <span>用户名</span>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={locked}
            autoFocus
          />
        </label>
        <label className="login-field">
          <span>密码(至少 6 位)</span>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={locked}
          />
        </label>
        <label className="login-field">
          <span>租户 (tenant)</span>
          <select value={tenant} onChange={(e) => setTenant(e.target.value)} disabled={locked}>
            {/* Keep the admin's own tenant selectable even before the list loads. */}
            {tenants.every((t) => t.name !== tenant) && <option value={tenant}>{tenant}</option>}
            {tenants.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="login-field">
          <span>角色</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} disabled={locked}>
            <option value="end_user">end_user(终端用户)</option>
            <option value="agent">agent(客服)</option>
            <option value="admin">admin(管理员)</option>
          </select>
        </label>
        {kbAdmin && (
          <label className="login-field">
            <span>知识库可见范围</span>
            <select value={clearance} onChange={(e) => setClearance(e.target.value as "public" | "internal")} disabled={locked}>
              <option value="public">公开 —— 只能看到公开内容(客户账号通常选这个)</option>
              <option value="internal">内部 —— 还能看到本租户的内部内容(内部员工)</option>
            </select>
          </label>
        )}
        {error !== null && <div className="login-error">{error}</div>}
        {ok !== null && <div className="admin-ok">{ok}</div>}
        <button
          className="login-btn"
          type="submit"
          disabled={locked || username.length === 0 || password.length < 6}
        >
          {busy ? "创建中…" : "创建用户"}
        </button>
        <button className="admin-cancel" type="button" onClick={onClose}>
          关闭
        </button>
      </form>
    </div>
  );
}
